import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField, Button, IconButton, Tooltip,
  Chip, Autocomplete, Link as MLink,
} from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import MicNoneRounded from '@mui/icons-material/MicNoneRounded';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded';
import AlarmRounded from '@mui/icons-material/AlarmRounded';
import PictureAsPdfRounded from '@mui/icons-material/PictureAsPdfRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import TableChartRounded from '@mui/icons-material/TableChartRounded';
import InsertDriveFileRounded from '@mui/icons-material/InsertDriveFileRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import Swal from 'sweetalert2';
import { createNote, updateNote, getNoteById, listProjects, createProject } from '../../services/api';

/* ===== CKEditor 5 ===== */
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  Bold, ClassicEditor, Essentials, Italic, Link, List, Paragraph, Undo,
} from 'ckeditor5';

type OptionItem = { _id: string; name: string };
type NoteFile = { _id: string; url: string; filename?: string; size?: number; mimetype?: string };

const CATEGORY_OPTIONS = [
  { name: 'Personal', color: '#0277bd' },
  { name: 'Work', color: '#6226A7' },
  { name: 'Ideas', color: '#F6A938' },
  { name: 'Meeting', color: '#CF1E52' },
  { name: 'Others', color: '#546E7A' },
];

const API_BASE = process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api';
const FILES_BASE = API_BASE.replace(/\/api\/?$/, '');
const toFileUrl = (u?: string) => (u ? (/^https?:\/\//i.test(u) ? u : `${FILES_BASE}${u}`) : '');

const asLocalInput = (val?: string | Date | null) => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const isMongoId = (s: string) => /^[a-f\d]{24}$/i.test(s);
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const normalizeOptions = (arr: any[]): OptionItem[] =>
  (arr || []).map((x: any) =>
    typeof x === 'string'
      ? { _id: x, name: x }
      : { _id: x?._id || x?.id || x?.value || x?.name, name: x?.name || x?.label || x?.title || '' }
  ).filter((o) => o._id && o.name);
const findById = (opts: OptionItem[], id?: string | null) => (id ? opts.find((o) => o._id === id) || null : null);
const findByName = (opts: OptionItem[], name?: string | null) =>
  (name ? opts.find((o) => o.name.toLowerCase().trim() === name.toLowerCase().trim()) || null : null);

const fileIcon = (m?: string) => {
  if (!m) return <InsertDriveFileRounded fontSize="small" />;
  if (m === 'application/pdf') return <PictureAsPdfRounded fontSize="small" />;
  if (m === 'text/plain') return <DescriptionRounded fontSize="small" />;
  if (m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || m === 'application/vnd.ms-excel')
    return <TableChartRounded fontSize="small" />;
  return <InsertDriveFileRounded fontSize="small" />;
};

const ckLiteConfig = {
  licenseKey: process.env.REACT_APP_CKEDITOR_LICENSE_KEY || 'GPL',
  plugins: [Bold, Essentials, Italic, Link, List, Paragraph, Undo],
  toolbar: ['bold', 'italic', '|', 'numberedList', 'bulletedList', '|', 'link', 'undo', 'redo'],
};

type CKLiteProps = { value: string; onChange: (html: string) => void };
const CKLite: React.FC<CKLiteProps> = ({ value, onChange }) => {
  const lastValueRef = useRef<string>(value || '');
  return (
    <CKEditor
      editor={ClassicEditor}
      data={value || ''}
      onChange={(_, editor) => {
        const html = editor.getData();
        if (html !== lastValueRef.current) {
          lastValueRef.current = html;
          onChange(html);
        }
      }}
      config={ckLiteConfig}
    />
  );
};

/* ==================== Quick-add dialog for Project ==================== */
function QuickAddProjectDialog({
  open, onClose, onCreate, options, onSelectExisting,
}: {
  open: boolean; onClose: () => void;
  onCreate: (name: string) => Promise<void>; options: OptionItem[]; onSelectExisting: (opt: OptionItem) => void;
}) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setName(''); setErr(''); setSaving(false); } }, [open]);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) return setErr('Name is required');
    const existing = findByName(options, clean);
    if (existing) { onSelectExisting(existing); onClose(); return; }
    setSaving(true);
    try {
      await onCreate(clean);
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to add');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Add Project</Typography>
        <TextField
          autoFocus fullWidth label="Project name" value={name}
          onChange={(e) => { setName(e.target.value); setErr(''); }}
          error={!!err} helperText={err || ' '}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Add'}</Button>
        </Box>
      </Box>
    </Dialog>
  );
}

/* ==================== Main dialog ==================== */
type Props = {
  open: boolean;
  noteSlug?: string | null;
  onClose: () => void;
  onSaved: () => void;
};

const NoteFormDialog = ({ open, noteSlug, onClose, onSaved }: Props) => {
  const [title, setTitle] = useState('');
  const [noteHtml, setNoteHtml] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [projectSel, setProjectSel] = useState<OptionItem | null>(null);
  const [reminder, setReminder] = useState('');

  const [projectOptions, setProjectOptions] = useState<OptionItem[]>([]);
  const [addProjectOpen, setAddProjectOpen] = useState(false);

  const [existingFiles, setExistingFiles] = useState<NoteFile[]>([]);
  const [removeFileIds, setRemoveFileIds] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [titleError, setTitleError] = useState('');
  const [reminderError, setReminderError] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = !!noteSlug;

  const resetForm = useCallback(() => {
    setTitle(''); setNoteHtml(''); setCategory(''); setTags([]); setProjectSel(null);
    setReminder(''); setExistingFiles([]); setRemoveFileIds([]); setNewFiles([]); setTitleError(''); setReminderError('');
  }, []);

  useEffect(() => {
    if (!open) return;
    listProjects().then((ps) => setProjectOptions(normalizeOptions(ps))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!noteSlug) {
      resetForm();
      return;
    }
    (async () => {
      try {
        const n = await getNoteById(noteSlug);
        const resolveToOption = (raw: any, opts: OptionItem[]): OptionItem | null => {
          if (!raw) return null;
          if (typeof raw === 'object') {
            const byId = findById(opts, raw._id || raw.id);
            if (byId) return byId;
            if ((raw._id || raw.id) && raw.name) return { _id: raw._id || raw.id, name: raw.name };
            return null;
          }
          if (typeof raw === 'string') {
            if (isMongoId(raw)) return findById(opts, raw);
            return findByName(opts, raw) || { _id: raw, name: raw };
          }
          return null;
        };

        setTitle(n.title || '');
        setNoteHtml(n.note ?? n.content ?? '');
        setCategory(n.category || '');
        setTags(Array.isArray(n.tags) ? n.tags : []);
        setProjectSel(resolveToOption(n.project, projectOptions));
        setReminder(asLocalInput(n.entryDate));
        setExistingFiles((n.images || []) as NoteFile[]);
      } catch {
        Swal.fire('Error', 'Failed to load note.', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noteSlug]);

  const onAddProject = async (name: string) => {
    await createProject(name);
    const ps = normalizeOptions(await listProjects());
    setProjectOptions(ps);
    setProjectSel(findByName(ps, name));
  };

  const handleFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const arr = Array.from(files);
    const valid = arr.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    if (valid.length < arr.length) Swal.fire('Invalid files', 'Attachments must be 10MB or smaller per file.', 'error');
    setNewFiles((prev) => [...prev, ...valid]);
  };

  const handleSave = async () => {
    const tErr = !title.trim()
      ? 'Title is required'
      : title.trim().length > 150 ? 'Title must be 150 characters or fewer' : '';
    const rErr = reminder && Number.isNaN(new Date(reminder).getTime()) ? 'Enter a valid date and time' : '';
    setTitleError(tErr);
    setReminderError(rErr);
    if (tErr || rErr) return;
    setSaving(true);

    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('note', noteHtml.trim());
    if (reminder) fd.append('entryDate', new Date(reminder).toISOString());
    if (category) fd.append('category', category);
    tags.forEach((t) => fd.append('tags[]', t));
    if (projectSel) fd.append('project', projectSel._id);
    removeFileIds.forEach((id) => fd.append('removeImageIds[]', id));
    newFiles.forEach((f) => fd.append('images', f));

    try {
      if (noteSlug) await updateNote(noteSlug, fd);
      else await createNote(fd);
      onSaved();
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Something went wrong.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const categoryMeta = CATEGORY_OPTIONS.find((c) => c.name === category);
  const accent = categoryMeta?.color || '#6226A7';
  const [dragOver, setDragOver] = useState(false);

  const setQuickReminder = (daysAhead: number, hour: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    d.setHours(hour, 0, 0, 0);
    setReminder(asLocalInput(d));
  };

  const fieldCardSx = {
    p: 1.75,
    borderRadius: '18px',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 6px 16px rgba(98,38,167,0.07)',
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: '28px', overflow: 'hidden', boxShadow: '0 40px 100px rgba(46,15,94,0.45)' } }}
    >
      {/* ===== Gradient header (tints with the chosen category) ===== */}
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', color: '#fff',
          px: { xs: 2, md: 3 }, py: 2.25,
          background: category
            ? `linear-gradient(120deg, ${accent} 0%, ${accent}D9 45%, #F6A938 150%)`
            : 'linear-gradient(120deg, #2E0F5E 0%, #6226A7 45%, #B06AB3 100%)',
          transition: 'background .4s ease',
        }}
      >
        <Box sx={{ position: 'absolute', width: 240, height: 240, right: -40, top: -120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,214,122,.6), transparent 65%)' }} />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.25), rgba(255,255,255,0) 55%)' }} />
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center', minWidth: 0 }}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: '18px', display: 'grid', placeItems: 'center', flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,.5), rgba(255,255,255,.15))',
                border: '1px solid rgba(255,255,255,.6)', boxShadow: '0 10px 22px rgba(0,0,0,.2)',
              }}
            >
              <EditRounded />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15, textShadow: '0 2px 10px rgba(0,0,0,.25)' }}>
                {isEdit ? 'Edit Note' : 'Add Note'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.92 }}>
                {isEdit ? 'Polish your note and save the changes.' : 'Capture your thoughts, ideas or anything...'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              onClick={onClose}
              sx={{
                color: '#fff', border: '2px solid rgba(255,255,255,.75)', px: 2.25, borderRadius: '999px',
                '&:hover': { bgcolor: 'rgba(255,255,255,.16)', transform: 'translateY(-1px)' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              startIcon={<SendRounded fontSize="small" />}
              sx={{
                px: 2.75, borderRadius: '999px', fontWeight: 800, color: accent,
                background: 'linear-gradient(180deg,#fff,#F1E8FF)',
                boxShadow: '0 10px 22px rgba(0,0,0,.28), inset 0 1px 0 #fff',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 14px 28px rgba(0,0,0,.34), inset 0 1px 0 #fff' },
                '&.Mui-disabled': { color: 'rgba(0,0,0,.4)', background: 'rgba(255,255,255,.7)' },
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' } }}>
          {/* ===== Main column ===== */}
          <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800, color: accent, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                Title
              </Typography>
              <TextField
                placeholder="Give your note a catchy title…"
                value={title}
                onChange={(e) => { setTitle(e.target.value); setTitleError(''); }}
                error={!!titleError}
                helperText={titleError}
                fullWidth
                autoFocus
                InputProps={{
                  sx: {
                    fontSize: 22, fontWeight: 800, borderRadius: '18px',
                    bgcolor: (t) => (t.palette.mode === 'light' ? '#FAF7FF' : 'action.hover'),
                    '& fieldset': { borderWidth: 2, borderColor: `${accent}55` },
                    '&:hover fieldset': { borderColor: `${accent}99` },
                    '&.Mui-focused fieldset': { borderColor: accent },
                    '&.Mui-focused': { boxShadow: `0 0 0 5px ${accent}22` },
                  },
                }}
                sx={{ mt: 0.5 }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800, color: accent, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                Your note
              </Typography>
              <Box
                sx={(theme) => ({
                  mt: 0.5,
                  border: '2px solid', borderColor: `${accent}55`, borderRadius: '18px', overflow: 'hidden',
                  bgcolor: 'background.paper',
                  transition: 'border-color .2s ease, box-shadow .2s ease',
                  '&:focus-within': { borderColor: accent, boxShadow: `0 0 0 5px ${accent}22` },
                  '& .ck-toolbar': {
                    border: 'none !important',
                    borderBottom: `1px solid ${theme.palette.divider} !important`,
                    background: `linear-gradient(180deg, ${accent}14, transparent) !important`,
                    padding: '6px 10px',
                  },
                  '& .ck.ck-button': { borderRadius: '10px' },
                  '& .ck.ck-button.ck-on': { background: `${accent}26 !important`, color: `${accent} !important` },
                  '& .ck-editor__editable': {
                    minHeight: 280, maxHeight: 420, border: 'none !important', boxShadow: 'none !important',
                    padding: '16px 18px !important', fontSize: 16, lineHeight: 1.65,
                    background: 'transparent !important',
                  },
                })}
              >
                <CKLite value={noteHtml} onChange={setNoteHtml} />
              </Box>
            </Box>

            {/* Attachments drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              sx={{
                borderRadius: '18px', p: 1.75,
                border: '2px dashed', borderColor: dragOver ? accent : `${accent}66`,
                bgcolor: dragOver ? `${accent}14` : 'transparent',
                transition: 'all .2s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  startIcon={<AttachFileRounded />}
                  sx={{
                    color: '#fff', px: 2, borderRadius: '999px',
                    background: `linear-gradient(135deg, ${accent}, ${accent}B3)`,
                    boxShadow: `0 8px 18px ${accent}55, inset 0 1px 0 rgba(255,255,255,.4)`,
                    '&:hover': { transform: 'translateY(-1px)' },
                  }}
                >
                  Attach files
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 160 }}>
                  or drag &amp; drop here · up to 10&nbsp;MB each
                </Typography>
                <Tooltip title="Voice input — coming soon">
                  <span><IconButton size="small" disabled><MicNoneRounded fontSize="small" /></IconButton></span>
                </Tooltip>
                <Tooltip title="AI writing assistant — coming soon">
                  <span>
                    <Button
                      disabled
                      size="small"
                      startIcon={<AutoAwesomeRounded fontSize="small" />}
                      sx={{
                        borderRadius: '999px', bgcolor: 'rgba(207,30,82,0.1)', color: '#CF1E52', fontWeight: 700,
                        '&.Mui-disabled': { color: '#CF1E52', opacity: 0.7 },
                      }}
                    >
                      Write with AI
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <input ref={fileInputRef} type="file" hidden multiple onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />

              {(existingFiles.length > 0 || newFiles.length > 0) && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {existingFiles.map((f) => (
                    <Chip
                      key={f._id}
                      icon={fileIcon(f.mimetype)}
                      label={<MLink href={toFileUrl(f.url)} underline="hover" target="_blank" rel="noopener">{f.filename || 'file'}</MLink>}
                      onDelete={() => { setExistingFiles((prev) => prev.filter((i) => i._id !== f._id)); setRemoveFileIds((prev) => [...prev, f._id]); }}
                      sx={{ bgcolor: `${accent}1A`, fontWeight: 600 }}
                    />
                  ))}
                  {newFiles.map((f, i) => (
                    <Chip
                      key={`${f.name}-${i}`}
                      icon={fileIcon(f.type)}
                      label={f.name}
                      onDelete={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      sx={{ bgcolor: `${accent}1A`, fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* ===== Sidebar ===== */}
          <Box
            sx={{
              p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 1.75,
              borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' },
              background: (t) =>
                t.palette.mode === 'light'
                  ? `linear-gradient(180deg, ${accent}12, #FAF7FF 60%)`
                  : `linear-gradient(180deg, ${accent}22, transparent)`,
            }}
          >
            <Box sx={fieldCardSx}>
              <SidebarLabel icon={CategoryRounded} label="Category" color="#6226A7" />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {CATEGORY_OPTIONS.map((c) => {
                  const active = category === c.name;
                  return (
                    <Box
                      key={c.name}
                      component="button"
                      type="button"
                      onClick={() => setCategory(active ? '' : c.name)}
                      sx={{
                        cursor: 'pointer', border: '2px solid', borderColor: active ? c.color : `${c.color}55`,
                        borderRadius: '999px', px: 1.5, py: 0.6, fontWeight: 800, fontSize: 13,
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        color: active ? '#fff' : c.color,
                        background: active ? `linear-gradient(135deg, ${c.color}, ${c.color}B3)` : 'transparent',
                        boxShadow: active ? `0 8px 16px ${c.color}55, inset 0 1px 0 rgba(255,255,255,.4)` : 'none',
                        transition: 'all .18s ease',
                        '&:hover': { transform: 'translateY(-1px)', bgcolor: active ? undefined : `${c.color}14` },
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: active ? '#fff' : c.color }} />
                      {c.name}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box sx={fieldCardSx}>
              <SidebarLabel icon={FolderRounded} label="Project" color="#2563EB" />
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={projectOptions}
                  value={projectSel}
                  onChange={(_, v) => setProjectSel(v)}
                  getOptionLabel={(o) => o?.name || ''}
                  isOptionEqualToValue={(o, v) => o._id === v._id}
                  renderInput={(params) => <TextField {...params} placeholder="Select project (optional)" />}
                />
                <Tooltip title="Add project">
                  <IconButton
                    onClick={() => setAddProjectOpen(true)}
                    sx={{ color: '#fff', background: 'linear-gradient(135deg,#2563EB,#60A5FA)', boxShadow: '0 6px 14px rgba(37,99,235,.4)', '&:hover': { opacity: 0.92 } }}
                  >
                    <AddRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Box sx={fieldCardSx}>
              <SidebarLabel icon={LocalOfferRounded} label="Tags" color="#EC4899" />
              <Autocomplete
                multiple
                freeSolo
                size="small"
                options={[]}
                value={tags}
                onChange={(_, v) => setTags((v as string[]).map((t) => t.trim()).filter(Boolean))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={`#${option}`}
                      size="small"
                      {...getTagProps({ index })}
                      key={option}
                      sx={{ bgcolor: '#EC489914', color: '#BE185D', fontWeight: 700 }}
                    />
                  ))
                }
                renderInput={(params) => <TextField {...params} placeholder="Type a tag and press Enter" />}
              />
            </Box>

            <Box sx={fieldCardSx}>
              <SidebarLabel icon={AlarmRounded} label="Reminder" color="#F59E0B" />
              <TextField
                size="small"
                fullWidth
                type="datetime-local"
                value={reminder}
                onChange={(e) => { setReminder(e.target.value); setReminderError(''); }}
                error={!!reminderError}
                helperText={reminderError}
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                {[
                  { label: 'Today 6 PM', d: 0, h: 18 },
                  { label: 'Tomorrow 9 AM', d: 1, h: 9 },
                  { label: 'Next week', d: 7, h: 9 },
                ].map((q) => (
                  <Chip
                    key={q.label}
                    label={q.label}
                    size="small"
                    clickable
                    onClick={() => setQuickReminder(q.d, q.h)}
                    sx={{ bgcolor: '#F59E0B1F', color: '#B45309', fontWeight: 700 }}
                  />
                ))}
                {reminder && (
                  <Chip label="Clear" size="small" clickable onClick={() => setReminder('')} onDelete={() => setReminder('')} sx={{ fontWeight: 700 }} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <QuickAddProjectDialog
        open={addProjectOpen}
        onClose={() => setAddProjectOpen(false)}
        options={projectOptions}
        onCreate={onAddProject}
        onSelectExisting={(opt) => setProjectSel(opt)}
      />
    </Dialog>
  );
};

const SidebarLabel = ({ icon: Icon, label, color }: { icon: React.ComponentType<any>; label: string; color: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
    <Box
      sx={{
        width: 28, height: 28, borderRadius: '10px', display: 'grid', placeItems: 'center', color: '#fff',
        background: `linear-gradient(135deg, ${color}, ${color}B3)`,
        boxShadow: `0 5px 12px ${color}55, inset 0 1px 0 rgba(255,255,255,.4)`,
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
    </Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{label}</Typography>
  </Box>
);

export default NoteFormDialog;
